import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";

// Components
import ButtonPill from "../../../../Buttons/ButtonPill";
import ButtonPillGradient from "../../../../Buttons/ButtonPillGradient";

// Assets
import PlayButton from "../../../../../Assets/SVGs/Icons/VideoPlayer/PlayButton";
import HeartEmpty from "../../../../../Assets/SVGs/Icons/UI/HeartEmpty";
import HeartFilled from "../../../../../Assets/SVGs/Icons/UI/HeartFilled";
import VolumeMuted from "../../../../../Assets/SVGs/Icons/VideoPlayer/VolumeMuted";
import Volume from "../../../../../Assets/SVGs/Icons/VideoPlayer/Volume";

// Models (NEW)
import {AuthUser} from "../../../../../Models/DTOs/AuthUser";

// Api / Hooks
import useFetchLatestEpisode from "../../../../../Hooks/Content/Watch/VideoSingle/useFetchLatestEpisode";
import useFavoriteSeriesStatus from "../../../../../Hooks/Content/Watch/Series/useFavoriteSeriesStatus";

// Constants
import {RoutePaths} from "../../../../../Constants/RoutePaths";
import {GlobalInterfaceContext} from "../../../../../Context/GlobalInterfaceContext";

interface SeriesHeroProps {
    title: string | undefined;
    description: string | undefined;
    background: string | undefined;
    userData: AuthUser | Record<string, never> | null;
    seriesId: number;
    trailerLink?: string;
}

const SeriesHero: React.FC<SeriesHeroProps> = (
    {
        title,
        description,
        background,
        userData,
        seriesId,
        trailerLink,
    }
) => {
    const
        authUser = (userData || {}) as AuthUser,
        {latest, loading: latestLoading} = useFetchLatestEpisode(seriesId),
        {isFav, loading: favLoading, toggle: toggleFav} = useFavoriteSeriesStatus(seriesId, authUser),
        {hasNotificationBar, hasUserInteracted} = useContext(GlobalInterfaceContext),
        backgroundVideoRef = useRef<HTMLVideoElement | null>(null),
        playerRef = useRef<Player | null>(null),
        [isMuted, setIsMuted] = useState(true),

        toggleMute = () => {
            const video = backgroundVideoRef.current;

            if (video) {
                video.muted = !isMuted;
                video.volume = isMuted ? 1.0 : 0;
                video.play().catch(() => {
                    // likely blocked by browser policy when unmuted autoplay
                    return;
                });

                setIsMuted(!isMuted);
            }
        };

    // Dispose synchronously when trailerLink goes away (prevents React/DOM mismatch)
    useLayoutEffect(() => {
        if (!trailerLink && playerRef.current) {
            try {
                if (!playerRef.current.isDisposed()) playerRef.current.dispose();
            } catch {
            }
            playerRef.current = null;

            const el = backgroundVideoRef.current;
            if (el) {
                el.pause();
                el.removeAttribute("src");
                el.load();
            }
        }
    }, [trailerLink]);

    useEffect(() => {
        if (hasUserInteracted && backgroundVideoRef.current) {
            // If you want to auto-unmute after user gesture, call toggleMute()
            // toggleMute();
        }
    }, [hasUserInteracted]);

    useEffect(() => {
        if (trailerLink && backgroundVideoRef.current && !playerRef.current) {
            const options = {
                autoplay: true,
                controls: false,
                muted: true,
                loop: true,
                preload: "auto",
                sources: [{src: trailerLink, type: "application/x-mpegURL"}],
            };

            playerRef.current = videojs(backgroundVideoRef.current, options);
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [trailerLink]);

    useEffect(() => {
        const video = backgroundVideoRef.current;
        if (!video) return;

        const handleEnded = () => {
            video.muted = true;
            video.volume = 0;
            video.loop = true;
            setIsMuted(true);

            video.play().catch(() => {
            });
        };

        video.addEventListener("ended", handleEnded);
        return () => {
            video.removeEventListener("ended", handleEnded);
        };
    }, [trailerLink]);

    useEffect(() => {
        const containerOne = document.querySelector(".series-hero__background-video-single") as HTMLElement | null;
        const containerTwo = document.querySelector(".series-hero__inner") as HTMLElement | null;
        if (!containerOne || !containerTwo) return;

        const updateSize = () => {
            const width = containerOne.offsetWidth;
            const height = (width * 9) / 30;
            containerOne.style.height = `${height}px`;
            containerTwo.style.height = `${height}px`;
        };

        updateSize();
        window.addEventListener("resize", updateSize);
        return () => {
            window.removeEventListener("resize", updateSize);
        };
    }, [trailerLink]);

    const isLoggedIn = !!authUser.id;

    return (
        <div className="layout layout--series-hero">
            <div className="series-hero__inner">
                <div className="series-hero__content">
                    <h1 className={title ? "is-not-loading" : "is-loading"}>
                        {title ? title : <div className="loader--side-to-side"/>}
                    </h1>

                    <div className="series-hero__description">
                        {description ? (
                            <p>{description}</p>
                        ) : (
                            <>
                                <div className="loader--side-to-side"/>
                                <div className="loader--side-to-side"/>
                                <div className="loader--side-to-side"/>
                            </>
                        )}
                    </div>

                    <div className="series-hero__button-wrapper">
                        <div className="series-hero__buttons u-relative_hidden">
                            {latest && latest.Id ? (
                                <ButtonPillGradient
                                    text="Play Latest Episode"
                                    className="series-hero__latest-button"
                                    background="linear-gradient(90deg, #4676E6 0%, #b760b4 100%)"
                                    backgroundHover="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"
                                    link={RoutePaths.Watch(latest.Id)}
                                    icon={PlayButton()}
                                />
                            ) : (
                                <div className="series-hero__button-loader loader--side-to-side"/>
                            )}

                            {isLoggedIn &&
                                (favLoading ? (
                                    <div className="series-hero__button-loader loader--side-to-side"/>
                                ) : (
                                    <ButtonPill
                                        label={isFav ? "Favorite" : "Add To Favorites"}
                                        className="series-hero__favorite-button"
                                        icon={isFav ? HeartFilled() : HeartEmpty()}
                                        onClick={toggleFav}
                                    />
                                ))}
                        </div>

                        {trailerLink && (
                            <div className="series-hero__trailer-buttons">
                                <ButtonPill
                                    label="Watch Trailer"
                                    link={RoutePaths.SeriesTrailer(seriesId)}
                                />

                                <button onClick={toggleMute} className="series-hero__mute-toggle">
                                    {isMuted ? VolumeMuted({iconFill: "white"}) : Volume({iconFill: "white"})}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Keep a stable wrapper; no <source> children; key forces clean remount of branch */}
            <div className="u-full_cover_absolute" key={trailerLink ? "has-trailer" : "no-trailer"}>
                {trailerLink ? (
                    <div
                        className={`series-hero__background-video u-full_cover_absolute ${
                            hasNotificationBar ? "has-notification-bar" : "has-no-notification-bar"
                        }`}
                    >
                        <video ref={backgroundVideoRef} autoPlay playsInline muted/>

                        <div className="series-hero__overlay u-full_cover_absolute"/>
                    </div>
                ) : (
                    <div
                        className="series-hero__background u-full_cover_absolute"
                        style={{background: `url("${background}") center/cover no-repeat`}}
                    >
                        <div className="series-hero__overlay u-full_cover_absolute"/>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SeriesHero;
